import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Context, Permission, Role } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SettingsService } from 'src/app/shared/services/settings.service';

@Component({
  selector: 'app-role-crud',
  templateUrl: './role-crud.component.html',
  styleUrls: ['./role-crud.component.scss'],
})
export class RoleCrudComponent implements OnInit {
  @Input() role: Role;
  @Input() roles: Role[] = [];
  public roleForm: FormGroup;
  public personas: string[] = [];
  public context: Context;
  public modules: Map<string, Permission[]> = new Map();
  public isAllSelected: boolean = true;
  public submitted: boolean = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private settingsService: SettingsService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.setRole(this.role);
    this.getAndSetModules(this.role?.permissionData);
    this.settingsService.getPersonas().subscribe((personas) => {
      this.personas = personas.filter((persona) => persona !== "ADMIN" && persona !== "CLIENT");
    });

  }

  private getAndSetModules(permissions: Permission[] = []) {
    this.settingsService.getDefaultModules().subscribe(modules => {
      Object.keys(modules).forEach((module, i) => {
        const features: any = Object.values(modules[module]).map(feature => ({
          id: permissions.find(p => p.module == feature)?.id || null,
          feature: module,
          privilege: permissions.find(p => p.module == feature)?.privilege || 'VIEW',
          disabled: permissions.find(p => p.module == feature)?.disabled ?? true,
          selected: permissions.some(p => p.module == feature),
          module: feature
        }));
        this.modules.set(module, features);
      });
      this.checkIfAllSelected();
    });
  }

  private setRole(role?: Role) {
    if (!role) role = {
      id: null,
      companyId: this.context.companyId,
      name: null,
      persona: null,
      disabled: false,
      permissions: [],
    } as Role;
    this.roleForm = this.fb.group({
      id: this.fb.control(role?.id || null),
      companyId: this.fb.control(role.companyId, [Validators.required]),
      name: this.fb.control(role.name, [Validators.required, Validators.pattern(RegExp.name)]),
      disabled: this.fb.control(role.disabled, []),
      persona: this.fb.control(role?.persona || '', [Validators.required])
    });
  }

  public saveRole(inProgress: any) {
    this.submitted = true;
    const role = this.roleForm.value as Role;
    let permissions: Permission[] = [];
    if (this.roles.some(r => r.name?.toLowerCase() === role.name?.toLowerCase())) {
      this.commonService.setToastr(400, `Role ${role.name} already exists!`);
      return;
    }
    this.modules?.forEach((module) => {
      module?.forEach(m => {
        if (m?.selected) {
          permissions.push(m);
        }
      });
    });
    role.permissionData = permissions;
    if (this.roleForm.valid) {
      if (permissions.length < 1) {
        this.commonService.setToastr(401, 'Please select a module.');
        return;
      }
      inProgress.value = true;
      if (role.id) {
        this.settingsService.updateRole(role.id, role).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              inProgress.value = false;
              this.activeModal.close(true);
              this.commonService.setToastr(200, `Role ${role.name} has been updated successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.settingsService.createRole(role).subscribe({
          next: (response: Role) => {
            if (response) {
              inProgress.value = false;
              this.activeModal.close(true);
              this.commonService.setToastr(200, `Role ${response.name} has been created successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  public onCheckboxChange(event, feature) {
    if (!event.target.checked) {
      feature.privilege = '';
    }
  }

  get r() {
    return this.roleForm.controls;
  }

  public checkAll(event, modules) {
    const checked: boolean = event.target.checked;
    modules.forEach((m) => {
      m.forEach((f) => {
        f.selected = checked;
        f.disabled = !checked
      });
    });
  }

  public checkIfAllSelected(checked?: any, feature?: any) {
    let array: boolean[] = [];
    this.modules.forEach((m, i) => {
      m.forEach((f: any) => {
        if (checked != null && feature != null) {
          if (f.module === feature.module) {
            f.disabled = !checked;
          }
        }
        array.push(f.selected);
      })
    });
    this.isAllSelected = array.every(e => e == true);
  }

  private deletePrivileges(ids:number[]){

  }
}
